import {
  CHASSIS_CODE_COLORS,
  type ChassisCodeColor,
  chassisCodeModels,
  defaultChassisCodeColor,
  normalizeChassisCode,
} from "@bt/domain";
import { useMutation, useQuery } from "@tanstack/react-query";
import { CarFrontIcon, CheckIcon, PlusIcon } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toLanguage } from "@/i18n";
import { useTRPC } from "@/lib/trpc";
import { cn } from "@/lib/utils";

interface ChassisCodeRef {
  id: string;
  name: string;
  color?: string;
}

// Written out in full so Tailwind generates every class.
const CHIP: Record<ChassisCodeColor, string> = {
  sky: "border-sky-200 bg-sky-50 text-sky-800 dark:border-sky-400/30 dark:bg-sky-400/10 dark:text-sky-200",
  blue: "border-blue-200 bg-blue-50 text-blue-800 dark:border-blue-400/30 dark:bg-blue-400/10 dark:text-blue-200",
  indigo:
    "border-indigo-200 bg-indigo-50 text-indigo-800 dark:border-indigo-400/30 dark:bg-indigo-400/10 dark:text-indigo-200",
  cyan: "border-cyan-200 bg-cyan-50 text-cyan-800 dark:border-cyan-400/30 dark:bg-cyan-400/10 dark:text-cyan-200",
  teal: "border-teal-200 bg-teal-50 text-teal-800 dark:border-teal-400/30 dark:bg-teal-400/10 dark:text-teal-200",
  emerald:
    "border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-400/30 dark:bg-emerald-400/10 dark:text-emerald-200",
  green:
    "border-green-200 bg-green-50 text-green-800 dark:border-green-400/30 dark:bg-green-400/10 dark:text-green-200",
  lime: "border-lime-200 bg-lime-50 text-lime-800 dark:border-lime-400/30 dark:bg-lime-400/10 dark:text-lime-200",
  violet:
    "border-violet-200 bg-violet-50 text-violet-800 dark:border-violet-400/30 dark:bg-violet-400/10 dark:text-violet-200",
  purple:
    "border-purple-200 bg-purple-50 text-purple-800 dark:border-purple-400/30 dark:bg-purple-400/10 dark:text-purple-200",
  amber:
    "border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-400/30 dark:bg-amber-400/10 dark:text-amber-200",
  yellow:
    "border-yellow-200 bg-yellow-50 text-yellow-800 dark:border-yellow-400/30 dark:bg-yellow-400/10 dark:text-yellow-200",
  orange:
    "border-orange-200 bg-orange-50 text-orange-800 dark:border-orange-400/30 dark:bg-orange-400/10 dark:text-orange-200",
  red: "border-red-200 bg-red-50 text-red-800 dark:border-red-400/30 dark:bg-red-400/10 dark:text-red-200",
  rose: "border-rose-200 bg-rose-50 text-rose-800 dark:border-rose-400/30 dark:bg-rose-400/10 dark:text-rose-200",
  pink: "border-pink-200 bg-pink-50 text-pink-800 dark:border-pink-400/30 dark:bg-pink-400/10 dark:text-pink-200",
  fuchsia:
    "border-fuchsia-200 bg-fuchsia-50 text-fuchsia-800 dark:border-fuchsia-400/30 dark:bg-fuchsia-400/10 dark:text-fuchsia-200",
  slate:
    "border-slate-200 bg-slate-50 text-slate-800 dark:border-slate-400/30 dark:bg-slate-400/10 dark:text-slate-200",
  stone:
    "border-stone-200 bg-stone-50 text-stone-800 dark:border-stone-400/30 dark:bg-stone-400/10 dark:text-stone-200",
};

const ICON: Record<ChassisCodeColor, string> = {
  sky: "text-sky-600 dark:text-sky-400",
  blue: "text-blue-600 dark:text-blue-400",
  indigo: "text-indigo-600 dark:text-indigo-400",
  cyan: "text-cyan-600 dark:text-cyan-400",
  teal: "text-teal-600 dark:text-teal-400",
  emerald: "text-emerald-600 dark:text-emerald-400",
  green: "text-green-600 dark:text-green-400",
  lime: "text-lime-600 dark:text-lime-400",
  violet: "text-violet-600 dark:text-violet-400",
  purple: "text-purple-600 dark:text-purple-400",
  amber: "text-amber-600 dark:text-amber-400",
  yellow: "text-yellow-600 dark:text-yellow-400",
  orange: "text-orange-600 dark:text-orange-400",
  red: "text-red-600 dark:text-red-400",
  rose: "text-rose-600 dark:text-rose-400",
  pink: "text-pink-600 dark:text-pink-400",
  fuchsia: "text-fuchsia-600 dark:text-fuchsia-400",
  slate: "text-slate-600 dark:text-slate-400",
  stone: "text-stone-600 dark:text-stone-400",
};

const DOT: Record<ChassisCodeColor, string> = {
  sky: "bg-sky-500",
  blue: "bg-blue-500",
  indigo: "bg-indigo-500",
  cyan: "bg-cyan-500",
  teal: "bg-teal-500",
  emerald: "bg-emerald-500",
  green: "bg-green-500",
  lime: "bg-lime-500",
  violet: "bg-violet-500",
  purple: "bg-purple-500",
  amber: "bg-amber-500",
  yellow: "bg-yellow-500",
  orange: "bg-orange-500",
  red: "bg-red-500",
  rose: "bg-rose-500",
  pink: "bg-pink-500",
  fuchsia: "bg-fuchsia-500",
  slate: "bg-slate-500",
  stone: "bg-stone-500",
};

const isColor = (value: string | undefined): value is ChassisCodeColor =>
  (CHASSIS_CODE_COLORS as readonly (string | undefined)[]).includes(value);

/** A code's colour as the server sent it, else its default. */
function colorOf(code: { name: string; color?: string }): ChassisCodeColor {
  return isColor(code.color) ? code.color : defaultChassisCodeColor(code.name);
}

/** One chassis code in its colour, so codes are told apart without reading them. */
export function ChassisCodeChip({
  code,
  className,
}: {
  code: { name: string; color?: string };
  className?: string;
}) {
  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center rounded border px-1 text-[11px] leading-4 font-semibold tabular",
        CHIP[colorOf(code)],
        className,
      )}
    >
      {code.name}
    </span>
  );
}

/** A colour swatch for pickers (settings, the picker's options). */
export function ChassisCodeDot({ color, className }: { color: string; className?: string }) {
  return (
    <span
      aria-hidden
      className={cn(
        "size-2.5 shrink-0 rounded-full",
        isColor(color) ? DOT[color] : "bg-muted-foreground/50",
        className,
      )}
    />
  );
}

/** "E90 E91 E92 +2": the chassis codes a part or template fits, each in its colour. */
export function ChassisCodeList({
  codes,
  max = 3,
  className,
}: {
  codes: ChassisCodeRef[];
  max?: number;
  className?: string;
}) {
  const { t } = useTranslation();
  const first = codes[0];
  if (!first) return null;
  const shown = codes.slice(0, max);
  const more = codes.length - shown.length;
  return (
    <span
      className={cn("inline-flex min-w-0 items-center gap-1 overflow-hidden", className)}
      title={codes.map((c) => c.name).join(", ")}
    >
      <CarFrontIcon className={cn("size-3.5 shrink-0", ICON[colorOf(first)])} aria-hidden />
      <span className="sr-only">{t("chassisCodes.title")}:</span>
      {shown.map((code) => (
        <ChassisCodeChip key={code.id} code={code} />
      ))}
      {more > 0 ? (
        <span className="shrink-0 text-xs text-muted-foreground">
          {t("chassisCodes.more", { count: more })}
        </span>
      ) : null}
    </span>
  );
}

/** Marks a part or template that fits the vehicle at hand; a long label truncates, not the name. */
export function FitsBadge({ code, className }: { code: string; className?: string }) {
  const { t } = useTranslation();
  const text = t("chassisCodes.fits", { code });
  return (
    <Badge tone="ok" className={cn("max-w-[55%] min-w-0 shrink", className)} title={text}>
      <CheckIcon />
      <span className="truncate">{text}</span>
    </Badge>
  );
}

/** Multi-select of the workspace's chassis codes; typing a new code offers to create it. */
export function ChassisCodePicker({
  value,
  onChange,
}: {
  value: string[];
  onChange: (value: string[]) => void;
}) {
  const { t, i18n } = useTranslation();
  const language = toLanguage(i18n.language);
  const trpc = useTRPC();
  const { data: codes = [] } = useQuery(trpc.chassisCodes.list.queryOptions());
  const create = useMutation(trpc.chassisCodes.create.mutationOptions());
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const toggle = (id: string) =>
    onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id]);
  const text = query.trim();
  const key = normalizeChassisCode(text);
  const lower = text.toLowerCase();
  const exact = codes.some(
    (code) => normalizeChassisCode(code.key) === key || normalizeChassisCode(code.name) === key,
  );
  const withModels = codes.map((code) => ({
    ...code,
    models: chassisCodeModels(code.key, language).join(" · "),
  }));
  const filtered = text
    ? withModels.filter(
        (code) =>
          normalizeChassisCode(code.name).includes(key) ||
          normalizeChassisCode(code.key).includes(key) ||
          code.models.toLowerCase().includes(lower),
      )
    : withModels;

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {codes
        .filter((code) => value.includes(code.id))
        .map((code) => (
          <Badge key={code.id} tone="outline" className="gap-1.5 py-1 pr-1 text-foreground">
            <ChassisCodeDot color={code.color} />
            {code.name}
            <button
              type="button"
              onClick={() => toggle(code.id)}
              aria-label={`${t("common.remove")}: ${code.name}`}
              className="rounded p-0.5 hover:bg-muted"
            >
              ×
            </button>
          </Badge>
        ))}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button type="button" variant="outline" size="sm">
            <CarFrontIcon />
            {t("chassisCodes.add")}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-72 p-0">
          <Command shouldFilter={false}>
            <CommandInput value={query} onValueChange={setQuery} placeholder={t("common.search")} />
            <CommandList>
              <CommandGroup>
                {filtered.map((code) => (
                  <CommandItem key={code.id} value={code.id} onSelect={() => toggle(code.id)}>
                    <CheckIcon
                      className={cn(value.includes(code.id) ? "opacity-100" : "opacity-0")}
                    />
                    <ChassisCodeChip code={code} />
                    {code.models ? (
                      <span className="truncate text-xs text-muted-foreground">{code.models}</span>
                    ) : null}
                  </CommandItem>
                ))}
                {text && !exact ? (
                  <CommandItem
                    value="__create"
                    onSelect={async () => {
                      const id = await create.mutateAsync({ name: text });
                      onChange([...value, id]);
                      setQuery("");
                    }}
                  >
                    <PlusIcon />
                    {t("chassisCodes.create", { name: text })}
                  </CommandItem>
                ) : null}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
